import React, { JSX, useEffect, useState } from 'react';
import { faPlusCircle, faMinusCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from 'reactstrap';

const PlusMinus = ({
  label,
  additionalLabel,
  maxCount,
  minCount,
  currentCount,
  onCountChange,
  disabled,
  validationFailed,
  occupancyErrorMessage,
  inputStepper,
  buttonsRole,
  plusButtonId
}: {
  label: string;
  additionalLabel?: string;
  minCount?: number;
  maxCount?: number;
  currentCount: number;
  onCountChange: (count: number) => void;
  disabled?: boolean;
  inputStepper?: boolean;
  validationFailed?: boolean;
  occupancyErrorMessage?: string;
  buttonsRole?: React.AriaRole;
  plusButtonId?: string;
}): JSX.Element => {
  const handleCountChange = (isPlus: boolean): void => {
    if (isPlus && (maxCount === undefined || currentCount < maxCount)) {
      onCountChange(currentCount + 1);
    } else if (!isPlus && (minCount === undefined || currentCount > minCount)) {
      onCountChange(currentCount - 1);
    }
  };

  const [tooltipOpen, setTooltipOpen] = useState(false);

  const onPlusButtonClick = () => {
    if (validationFailed) {
      setTooltipOpen(true);
    } else {
      handleCountChange(true);
    }
  };

  const handleMouseLeave = () => setTooltipOpen(false);

  const handleBlur = () => {
    setTooltipOpen(false);
  };

  useEffect(() => {
    const plusButton = document.getElementById(plusButtonId ? plusButtonId : 'plus-button');
    if (plusButton) {
      plusButton.addEventListener('blur', handleBlur);
    }
    return () => {
      if (plusButton) {
        plusButton.removeEventListener('blur', handleBlur);
      }
    };
  }, [plusButtonId]);

  if (inputStepper) {
    return (
      <div className={`plus-minus${disabled ? ' plus-minus--disabled' : ''}`}>
        {additionalLabel ? (
          <div>
            <div className="plus-minus__label">{label}</div>
            <div className="plus-minus__additional-label">{additionalLabel}</div>
          </div>
        ) : (
          <span className="plus-minus__label">{label}</span>
        )}
        <div className="plus-minus__counter">
          <span className="plus-minus__count">{currentCount}</span>
          <div className="plus-minus__buttons">
            <button
              id={plusButtonId ? plusButtonId : 'plus-button'}
              role={buttonsRole}
              onClick={(): void => (!disabled ? onPlusButtonClick() : undefined)}
              onMouseLeave={handleMouseLeave}
            >
              +
            </button>
            <Tooltip
              isOpen={tooltipOpen}
              target={plusButtonId ? plusButtonId : 'plus-button'}
              placement="top"
              trigger="click"
              z-index={100}
            >
              {occupancyErrorMessage}
            </Tooltip>
            <button onClick={(): void => (!disabled ? handleCountChange(false) : undefined)}>
              -
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={`plus-minus${disabled ? ' plus-minus--disabled' : ''}`}>
      {additionalLabel ? (
        <div>
          <div className="plus-minus__label">{label}</div>
          <div className="plus-minus__additional-label">{additionalLabel}</div>
        </div>
      ) : (
        <span className="plus-minus__label">{label}</span>
      )}
      <span className="plus-minus__buttons">
        <button
          role={buttonsRole}
          onClick={(): void => (!disabled ? handleCountChange(false) : undefined)}
        >
          <FontAwesomeIcon icon={faMinusCircle} />
        </button>
        <span>{currentCount}</span>
        <button
          role={buttonsRole}
          onClick={(): void => (!disabled ? handleCountChange(true) : undefined)}
        >
          <FontAwesomeIcon icon={faPlusCircle} />
        </button>
      </span>
    </div>
  );
};

export default PlusMinus;
